import { OnInit, OnDestroy } from '@angular/core';
import { Component } from '@angular/core';
import { environment } from 'src/envs/environnement';
import { Menu, MyMenuService } from '../renew/services/my-menu-service';
import { GeneralService } from '../renew/services/general-service';
import { CurrentUserService } from '../renew/services/current-user.service';
import { StructureCouranteService } from '../renew/services/structure-courante.service';
import { Subject, takeUntil } from 'rxjs';

interface MenuItem {
    label: string;
    icon?: string;
    routerLink?: string[]; // PrimeNG uses array for routerLink
    items?: MenuItem[];
    separator?: boolean;
}

@Component({
    selector: 'app-menu',
    templateUrl: './app.menu.component.html'
})
export class AppMenuComponent implements OnInit, OnDestroy {

    model: any[] = [];
    currentUser = JSON.parse(sessionStorage.getItem("currentUser") ?? 'null');
    menuErrorMessage: string | null = null;
    private destroy$ = new Subject<void>();

    constructor(
        private menuService: MyMenuService,
        private generalService: GeneralService,
        private currentUserService: CurrentUserService,
        private structureCouranteService: StructureCouranteService
    ) { }

    ngOnInit() {
        this.structureCouranteService.structureId$
            .pipe(takeUntil(this.destroy$))
            .subscribe(id => {
                if (!id) {
                    this.model = [];
                    this.menuErrorMessage = "Aucune entreprise selectionnee. Choisissez une entreprise pour acceder au menu.";
                    return;
                }
                this.menuErrorMessage = null;
                this.chargerMenu(id);
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private chargerMenu(structureId: string): void {
        const appartenanceEndpoint = "structures-appartenance/by-structure-id";
        this.generalService.getById(appartenanceEndpoint, structureId).subscribe({
            next: (response) => {
                if (!response?.role?.name) {
                    this.menuErrorMessage = "Aucun role ne vous est attribue sur cette entreprise. Contactez votre administrateur.";
                    return;
                }
                this.currentUserService.setPermissionsFromRole(response.role);
                this.menuService.getMenuById(response.role.name).subscribe({
                    next: (menus: Menu[]) => {
                        this.model = this.transformMenus(menus);
                        if (!this.model.length) {
                            this.menuErrorMessage = "Aucun menu n'est configure pour votre role sur cette entreprise.";
                        }
                    },
                    error: (error) => {
                        console.error("Erreur lors du chargement des menus :", error);
                        this.menuErrorMessage = "Impossible de charger le menu. Reessayez ou contactez votre administrateur.";
                    }
                });
            },
            error: (error) => {
                console.error("Erreur lors de la récupération de l'appartenance structure :", error);
                if (error?.status === 403) {
                    this.menuErrorMessage = "L'abonnement de cette entreprise a expire. Contactez votre administrateur.";
                } else {
                    this.menuErrorMessage = "Impossible de verifier vos droits sur cette entreprise.";
                }
            }
        });
    }

    private transformMenus(menus: Menu[]): any[] {
        return menus.map(menu => {
            const hasSubMenus = menu.sousMenus && menu.sousMenus.trim() !== "";

            if (hasSubMenus) {
                return {
                    label: menu.libelle,
                    icon: menu.icon,
                    isMain: menu.isMain, // Preserving existing property, though unsure if used by PrimeNG
                    items: this.parseSubMenus(menu)
                };
            } else {
                return {
                    label: menu.libelle,
                    icon: menu.icon,
                    routerLink: [menu.route]
                };
            }
        });
    }

    private parseSubMenus(menu: Menu): any[] {
        if (!menu.sousMenus) return [];

        const labels = menu.sousMenus.split(";").map(s => s.trim());
        const icons = (menu.sousMenusIcon || "").split(";").map(s => s.trim());
        const routes = (menu.sousMenusRouter || "").split(";").map(s => s.trim());

        return labels.map((label, index) => {
            // Robust fallback: if icon/route at index doesn't exist, use default or empty
            const icon = icons[index] || "pi pi-circle";
            const route = routes[index] || "#";

            return {
                label: label,
                icon: icon,
                routerLink: [route]
            };
        }).filter(item => item.label !== ""); // Filter out empty labels if any double semicolons exist
    }
}
